import { changeTypes, eventIconTypes } from '@/common/components/HistoryDrawer/constants'

import {
  copyBaseKeyLabels,
  fieldKeyLabels,
  languageLabels,
  legacyFieldKeyPrefixes,
  settingsFieldKeys,
  widgetTextBaseLabels,
} from './fieldKeys'

const iconByChangeType = {
  [changeTypes.objectCreated]: eventIconTypes.plus,
  [changeTypes.objectDeleted]: eventIconTypes.trash,
  [changeTypes.objectUpdated]: eventIconTypes.edit,
  [changeTypes.fieldUpdate]: eventIconTypes.edit,
}

const getLanguageSuffixedLabel = (fieldKey, t) => {
  const langCodes = Object.keys(languageLabels)
  for (const lang of langCodes) {
    if (fieldKey.endsWith(`_${lang}`)) {
      const baseKey = fieldKey.slice(0, -(lang.length + 1))
      const baseLabel = copyBaseKeyLabels[baseKey] || widgetTextBaseLabels[baseKey]
      if (baseLabel) {
        const langLabel = languageLabels[lang]
        return `${t(baseLabel)} (${t(langLabel)})`
      }
    }
  }
  return null
}

const getCurrentFieldKey = fieldKey => {
  const [prefix, ...fieldPath] = fieldKey.split('.')
  const currentPrefix = Object.keys(legacyFieldKeyPrefixes).find(key => legacyFieldKeyPrefixes[key] === prefix)

  if (!currentPrefix) return fieldKey

  return [currentPrefix, ...fieldPath].join('.')
}

export const formatFieldLabel = (fieldKey, t) => {
  if (!fieldKey) return ''
  const translationKey = fieldKeyLabels[getCurrentFieldKey(fieldKey)]
  if (translationKey && t) return t(translationKey)
  if (t) {
    const suffixedLabel = getLanguageSuffixedLabel(fieldKey, t)
    if (suffixedLabel) return suffixedLabel
  }
  const parts = fieldKey.split('.')
  const key = parts.length > 1 ? parts.slice(1).join('_') : parts[0]
  const label = key.replace(/_/g, ' ')
  return label.charAt(0).toUpperCase() + label.slice(1)
}

const getEventIcon = changes => {
  const firstChange = (changes || [])[0]
  const changeType = firstChange?.change_type || changeTypes.fieldUpdate

  return iconByChangeType[changeType] || eventIconTypes.edit
}

const getEventTitle = (changes, t) => {
  const safeChanges = changes || []
  const hasFieldUpdates = safeChanges.some(c => c.change_type === changeTypes.fieldUpdate)

  if (hasFieldUpdates) return t('Configuración actualizada')

  const firstChange = safeChanges[0]
  if (!firstChange) return t('Configuración actualizada')

  const objectType = formatFieldLabel(firstChange.field_key, t)
  const actionMap = {
    [changeTypes.objectCreated]: t('creado'),
    [changeTypes.objectUpdated]: t('actualizado'),
    [changeTypes.objectDeleted]: t('eliminado'),
  }
  const action = actionMap[firstChange.change_type] || ''

  return `${objectType} ${action}`.trim()
}

const getEventDetails = (changes, t) => {
  const safeChanges = changes || []

  return safeChanges.map(change => {
    if (change.change_type === changeTypes.fieldUpdate) {
      return { label: formatFieldLabel(change.field_key, t) }
    }

    const name = change.object_name || formatFieldLabel(change.field_key, t)
    const label = change.object_internal_id ? `${name} (${change.object_internal_id})` : name

    return { label }
  })
}

export const formatSettingsEvent = (event, t) => {
  const changes = event.changes || []

  return {
    icon: getEventIcon(changes),
    title: getEventTitle(changes, t),
    subtitle: null,
    expandable: true,
    details: getEventDetails(changes, t),
  }
}

export const getFieldKeysForTab = (tabSelected, pathname) => {
  if (!tabSelected) return []

  if (tabSelected.children) {
    const activeChild = tabSelected.children.find(child => pathname.startsWith(child.path))
    if (activeChild) {
      return settingsFieldKeys[activeChild.id] || []
    }
  }

  return settingsFieldKeys[tabSelected.id] || []
}

const isValidUUID = segment => /^[0-9a-f]{32}$/i.test(segment)

export const getDetailRouteInfo = (tabSelected, pathname) => {
  if (!tabSelected?.path) return { objectId: null, isCreation: false }

  const segment = pathname.split('/').pop()

  if (isValidUUID(segment)) return { objectId: segment, isCreation: false }

  return { objectId: null, isCreation: !pathname.endsWith(tabSelected.path) }
}
